import { useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import posService from "../../services/pos/pos.service";
import { generateOutwardChallanPDF } from "./OutwardChallan-PDF";
import POSEWayBill from "./POSEWayBill";

interface OutwardChallan {
  outwardId: number;
  movementType: string;
  createdDate: string;
  productName: string;
  quantity: number;
  sourceType: string;
  sourceValue: string | null;
  documentDetails: string;
  note: string;
  challanNo?: string | null;
}

interface OutwardChallanTableProps {
  data: OutwardChallan[] | null;
  refreshData: () => void;
  startDate?: Date | null;
  endDate?: Date | null;
  challanCount: number;
}

export const OutwardChallan = ({
  data,
  refreshData,
  // startDate,
  // endDate,
  challanCount,
}: OutwardChallanTableProps) => {
  const tableData = data || [];
  const toast = useRef<Toast>(null);
  const [selectedRows, setSelectedRows] = useState<OutwardChallan[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  // const [isViewing, setIsViewing] = useState(false);
  const [showEwayDialog, setShowEwayDialog] = useState(false);
  const [ewayOrder, setEwayOrder] = useState<any>(null);

  const sortedData = [...tableData].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const typeBody = (rowData: OutwardChallan) => {
    let severity: "info" | "success" | "warning" | undefined;
    switch (rowData.movementType) {
      case "Inward":
        severity = "success";
        break;
      case "Outward":
        severity = "info";
        break;
      case "Adjust":
        severity = "warning";
        break;
      default:
        severity = undefined;
    }
    return <Tag value={rowData.movementType} severity={severity} />;
  };

  const dateBody = (rowData: OutwardChallan) => {
    const dt = new Date(rowData.createdDate);
    return dt.toLocaleDateString("en-GB");
  };

  // ✅ Generate Outward Challan
  const handleGenerate = async () => {
    const safeSelectedRows = selectedRows || [];
    if (safeSelectedRows.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select at least one record to generate challan.",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const uniqueSources = Array.from(
        new Set(
          safeSelectedRows
            .map((row) => row.sourceValue)
            .filter((val): val is string => val !== null)
        )
      );

      for (const source of uniqueSources) {
        const selectedIds = safeSelectedRows
          .filter((row) => row.sourceValue === source)
          .map((row) => row.outwardId);

        const payload = { outwardIds: selectedIds };
        const response = await posService.generateOutwardChallan(payload);

        generateOutwardChallanPDF({
          categorySummaries: response?.categorySummaries || [],
          lineItems: response?.lineItems || [],
          challanNo: response?.challanNo?.toString() || "",
          fromAddress: response?.fromAddress || "",
          toAddress: response?.toAddress || "",
          generatedBy: response?.generatedBy || "",
        });

        setEwayOrder(response);
      }

      setSelectedRows([]);
      refreshData();

      toast.current?.show({
        severity: "success",
        summary: "Challan Generated",
        detail: "Outward Challan successfully created.",
      });
    } catch (error) {
      console.error("Error generating challan:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate challan.",
      });
    } finally {
      setIsGenerating(false);
    }
  };
 
  // ✅ Fetch today's challans excluding those that already have E-Way Bill
  const fetchTodaysChallan = async () => {
    try {
      const today = new Date();
      const challans = await posService.viewOutwardChallan(today, today);

      if (!challans || challans.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "No Data",
          detail: "No challan found for today.",
        });
        return null;
      }

      // Filter out challans where hasEwayBill is true
      const eligibleChallans = challans.filter((c: any) => !c.hasEwayBill);

      if (eligibleChallans.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "No E-Way Bill Eligible Challans",
          detail: "All today's challans already have an E-Way Bill.",
        });
        return null;
      }
      return eligibleChallans[0];
    } catch (error) {
      console.error("Error fetching today's challan:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch challan details.",
      });
      return null;
    }
  };

  // ✅ Generate E-Way Bill only for eligible challans
  const handleGenerateEway = async () => {
    const challan = await fetchTodaysChallan();
    if (!challan) return;   
    setEwayOrder(challan);
    setShowEwayDialog(true);
  };


  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        {/* 🔹 Action Buttons */}
        <div className="flex justify-content-end gap-2 mb-2">

          <Button
            label={isGenerating ? "Generating..." : "Generate Outward Challan"}
            icon="pi pi-file"
            disabled={isGenerating || selectedRows.length === 0}
            loading={isGenerating}
            onClick={handleGenerate}
            severity="success"
          />

          <Button
             label={`Generate E-WayBill${challanCount ? ` (${challanCount})` : ""}`}
            icon="pi pi-truck"
            severity="info"
            onClick={handleGenerateEway}
          />
        </div>

        {/* 🔹 Data Table */}
        <DataTable
          value={sortedData}
          paginator
          rows={10}
          stripedRows
          showGridlines
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value || [])}
          dataKey="outwardId"
          selectionMode="checkbox"
          rowsPerPageOptions={[10, 25, 50, 100]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column header="Type" body={typeBody} />
          <Column header="Date" body={dateBody} />
          <Column field="productName" header="Product" sortable />
          <Column field="quantity" header="Quantity" sortable />
          <Column field="sourceType" header="Source Type" sortable />
          <Column field="sourceValue" header="Source / Reference" sortable />
          <Column field="documentDetails" header="Document Details" sortable />
          <Column field="note" header="Note" sortable />
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        </DataTable>
      </div>

      {/* 🔹 E-Way Bill Dialog */}
      <Dialog
        header="Generate E-Way Bill"
        visible={showEwayDialog}
        style={{ width: "50vw" }}
        modal
        onHide={() => setShowEwayDialog(false)}
      >
        {ewayOrder && (
          <POSEWayBill
            order={ewayOrder}
            onCancel={() => setShowEwayDialog(false)}
            onSuccess={() => {
              setShowEwayDialog(false);
              refreshData();
              toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: "E-Way Bill generated successfully.",
              });
            }}
          />
        )}
      </Dialog>
    </div>
  );
};
